import type { KeyValueStorage } from '../progress';

export const RECOVERY_EPOCH = '20261002T0600Z-r1';
export const RECOVERY_BACKUP_KEY = `paltown.recoveryBackup.${RECOVERY_EPOCH}`;
/** 表示キャッシュを消したことだけを示す marker。 */
export const RECOVERY_COMPLETE_KEY = `paltown.recoveryComplete.${RECOVERY_EPOCH}`;
/** サーバー権威の初回読戻しまで成功したことを示す marker。 */
export const RECOVERY_HYDRATED_KEY = `paltown.recoveryHydrated.${RECOVERY_EPOCH}`;

/** 初回のサーバー読戻しを待つ操作へ返す、分類可能な失敗。 */
export class RecoveryHydrationError extends Error {
  constructor() {
    super('復旧のためのサーバー読戻しが完了していません');
    this.name = 'RecoveryHydrationError';
  }
}

/**
 * 復旧初回の読戻しが成功するまで、書込みと開始照会を止める。
 * 失敗は待機を残さず呼出元へ返し、次の読戻し成功で再び通す。
 */
export class RecoveryStartGate {
  private state: 'pending' | 'failed' | 'ready' = 'pending';
  private waiters: Array<{ resolve: () => void; reject: (error: Error) => void }> = [];

  wait = (): Promise<void> => {
    if (this.state === 'ready') return Promise.resolve();
    if (this.state === 'failed') return Promise.reject(new RecoveryHydrationError());
    return new Promise<void>((resolve, reject) => {
      this.waiters.push({ resolve, reject });
    });
  };

  complete(success: boolean): void {
    this.state = success ? 'ready' : 'failed';
    const waiters = this.waiters;
    this.waiters = [];
    for (const waiter of waiters) {
      if (success) waiter.resolve();
      else waiter.reject(new RecoveryHydrationError());
    }
  }
}

/** 本番だけに復元手順を適用する。エミュレータ接続は本番プロジェクト名でも除外する。 */
export function requiresRecovery(projectId: string, emulator: unknown): boolean {
  return projectId === 'paltyping-cb5ab' && emulator === null;
}

interface StorageWithKeys extends KeyValueStorage {
  readonly length: number;
  key(index: number): string | null;
}

type RecoveryResult = 'not-needed' | 'complete' | 'blocked';

function isStorageWithKeys(storage: KeyValueStorage | null): storage is StorageWithKeys {
  return storage !== null && typeof (storage as Partial<StorageWithKeys>).key === 'function';
}

function parseBackup(raw: string | null): Record<string, string> | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    const entries = (parsed as Record<string, unknown>).entries;
    if (typeof entries !== 'object' || entries === null || Array.isArray(entries)) return null;
    return Object.fromEntries(
      Object.entries(entries).filter(
        (entry): entry is [string, string] =>
          typeof entry[0] === 'string' && typeof entry[1] === 'string',
      ),
    );
  } catch {
    return null;
  }
}

function sameEntries(left: Record<string, string>, right: Record<string, string>): boolean {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key])
  );
}

/**
 * 復旧前の表示キャッシュと outbox を端末内に保全してから消す。
 * marker が無い途中状態では既存 backup を再利用するため、再読込しても元データを失わない。
 */
export function backupAndClearRecoveryCache(storage: KeyValueStorage | null): RecoveryResult {
  if (!isStorageWithKeys(storage)) return 'blocked';
  try {
    if (storage.getItem(RECOVERY_COMPLETE_KEY) === RECOVERY_EPOCH) return 'complete';

    let entries = parseBackup(storage.getItem(RECOVERY_BACKUP_KEY));
    if (entries === null) {
      entries = {};
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key?.startsWith('paltyping.')) {
          const value = storage.getItem(key);
          if (value !== null) entries[key] = value;
        }
      }
      const backup = JSON.stringify({ version: 1, recoveryEpoch: RECOVERY_EPOCH, entries });
      storage.setItem(RECOVERY_BACKUP_KEY, backup);
      const restored = parseBackup(storage.getItem(RECOVERY_BACKUP_KEY));
      if (restored === null || !sameEntries(entries, restored)) return 'blocked';
    }

    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
      (key): key is string => key?.startsWith('paltyping.') === true,
    );
    for (const key of keys) storage.removeItem(key);
    storage.setItem(RECOVERY_COMPLETE_KEY, RECOVERY_EPOCH);
    return storage.getItem(RECOVERY_COMPLETE_KEY) === RECOVERY_EPOCH ? 'complete' : 'blocked';
  } catch {
    return 'blocked';
  }
}

/** cache 初期化済みでも、読戻し成功前だけ初回復旧の送信抑止を続ける。 */
export function needsRecoveryHydration(storage: KeyValueStorage | null): boolean {
  try {
    return storage?.getItem(RECOVERY_HYDRATED_KEY) !== RECOVERY_EPOCH;
  } catch {
    return true;
  }
}

/** `readProgress` の反映が成功したあとにだけ記録する。失敗時は次回も読戻す。 */
export function markRecoveryHydrated(storage: KeyValueStorage | null): boolean {
  try {
    storage?.setItem(RECOVERY_HYDRATED_KEY, RECOVERY_EPOCH);
    return storage?.getItem(RECOVERY_HYDRATED_KEY) === RECOVERY_EPOCH;
  } catch {
    return false;
  }
}
